import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiDownload,
  FiFileText,
  FiExternalLink,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiFilter,
  FiSearch,
  FiCalendar,
  FiDollarSign,
  FiRefreshCw
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { getInvoices } from '../../api/payments';
import { useToast } from '../../context/ToastContext';

interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

type StatusFilter = 'all' | 'paid' | 'open' | 'void' | 'uncollectible';
type DateFilter = 'all' | 'month' | 'quarter' | 'year';

const InvoicesPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInvoices(50); // Récupérer les 50 dernières factures
      setInvoices(response.invoices || []);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des factures');
      toast.error('Impossible de charger les factures');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    let result = [...invoices];

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(inv => inv.status === statusFilter);
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();

      switch (dateFilter) {
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoff.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoff.setFullYear(now.getFullYear() - 1);
          break;
      }

      result = result.filter(inv => new Date(inv.created * 1000) >= cutoff);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv =>
        inv.number?.toLowerCase().includes(query) ||
        inv.id.toLowerCase().includes(query) ||
        formatAmount(inv.amount, inv.currency).toLowerCase().includes(query)
      );
    }

    return result;
  }, [invoices, statusFilter, dateFilter, searchQuery]);

  // Calculate statistics
  const stats = useMemo(() => {
    const paidInvoices = invoices.filter(inv => inv.status === 'paid');
    const openInvoices = invoices.filter(inv => inv.status === 'open');

    const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    const totalPending = openInvoices.reduce((sum, inv) => sum + inv.amount, 0);

    return {
      totalInvoices: invoices.length,
      paidCount: paidInvoices.length,
      openCount: openInvoices.length,
      totalPaid,
      totalPending,
      currency: invoices[0]?.currency || 'eur'
    };
  }, [invoices]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { labelKey: string; icon: React.ReactElement; className: string }> = {
      paid: {
        labelKey: 'invoices.status.paid',
        icon: <FiCheckCircle className="w-4 h-4" />,
        className: 'bg-green-100 text-green-800 border-green-200',
      },
      open: {
        labelKey: 'invoices.status.open',
        icon: <FiClock className="w-4 h-4" />,
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      },
      void: {
        labelKey: 'invoices.status.void',
        icon: <FiXCircle className="w-4 h-4" />,
        className: 'bg-gray-100 text-gray-800 border-gray-200',
      },
      uncollectible: {
        labelKey: 'invoices.status.uncollectible',
        icon: <FiAlertCircle className="w-4 h-4" />,
        className: 'bg-red-100 text-red-800 border-red-200',
      },
      draft: {
        labelKey: 'invoices.status.draft',
        icon: <FiFileText className="w-4 h-4" />,
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      },
    };

    const config = statusConfig[status] || statusConfig.open;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${config.className}`}>
        {config.icon}
        {t(config.labelKey)}
      </span>
    );
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter !== 'all' || dateFilter !== 'all' || searchQuery.trim() !== '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('invoices.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <FiXCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">{t('invoices.error.title')}</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchInvoices}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            {t('invoices.error.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('invoices.header.title')}</h1>
          <p className="text-gray-600">
            {t('invoices.header.subtitle')}
          </p>
        </div>
        <button
          onClick={fetchInvoices}
          disabled={loading}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          title={t('invoices.refresh')}
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('invoices.refresh')}
        </button>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <FiFileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('invoices.empty.title')}</h3>
          <p className="text-gray-600">
            {t('invoices.empty.description')}
          </p>
        </div>
      ) : (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('invoices.stats.totalBilled')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatAmount(stats.totalPaid, stats.currency)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <FiDollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('invoices.stats.pending')}</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatAmount(stats.totalPending, stats.currency)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <FiClock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('invoices.stats.paidCount')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.paidCount}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <FiCheckCircle className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t('invoices.stats.totalInvoices')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalInvoices}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <FiFileText className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px] max-w-sm">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('invoices.filters.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <FiFilter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white text-gray-900"
                >
                  <option value="all">{t('invoices.filters.allStatuses')}</option>
                  <option value="paid">{t('invoices.filters.paid')}</option>
                  <option value="open">{t('invoices.filters.open')}</option>
                  <option value="void">{t('invoices.filters.void')}</option>
                  <option value="uncollectible">{t('invoices.filters.uncollectible')}</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <FiCalendar className="w-4 h-4 text-gray-400" />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-white text-gray-900"
                >
                  <option value="all">{t('invoices.filters.allDates')}</option>
                  <option value="month">{t('invoices.filters.lastMonth')}</option>
                  <option value="quarter">{t('invoices.filters.last3Months')}</option>
                  <option value="year">{t('invoices.filters.thisYear')}</option>
                </select>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  {t('invoices.filters.clearFilters')}
                </button>
              )}
            </div>

            {/* Active filters summary */}
            {hasActiveFilters && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  {t('invoices.filters.resultsCount', { count: filteredInvoices.length })}
                  {statusFilter !== 'all' && ` • ${t('invoices.filters.statusLabel')}: ${t(`invoices.filters.${statusFilter}`)}`}
                  {dateFilter !== 'all' && ` • ${t('invoices.filters.periodLabel')}: ${t(`invoices.filters.${dateFilter === 'month' ? 'lastMonth' : dateFilter === 'quarter' ? 'last3Months' : 'thisYear'}`)}`}
                  {searchQuery && ` • ${t('invoices.filters.searchLabel')}: "${searchQuery}"`}
                </p>
              </div>
            )}
          </div>

          {/* Invoices Table */}
          {filteredInvoices.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
              <FiSearch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('invoices.noResults.title')}</h3>
              <p className="text-gray-600 mb-4">
                {t('invoices.noResults.description')}
              </p>
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {t('invoices.noResults.resetFilters')}
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('invoices.table.date')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('invoices.table.number')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('invoices.table.status')}
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('invoices.table.amount')}
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('invoices.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FiCalendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {formatShortDate(invoice.created)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FiFileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900 font-mono">
                              {invoice.number || invoice.id.substring(0, 12)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {formatAmount(invoice.amount, invoice.currency)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {invoice.hosted_invoice_url && (
                              <a
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                                title={t('invoices.actions.viewOnline')}
                              >
                                <FiExternalLink className="w-4 h-4" />
                                <span className="hidden sm:inline">{t('invoices.actions.view')}</span>
                              </a>
                            )}
                            {invoice.invoice_pdf && (
                              <a
                                href={invoice.invoice_pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors"
                                title={t('invoices.actions.downloadPdf')}
                              >
                                <FiDownload className="w-4 h-4" />
                                <span className="hidden sm:inline">PDF</span>
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer info */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {t('invoices.footer.showing', { filtered: filteredInvoices.length, total: invoices.length })}
                </p>
                <p className="text-sm text-gray-500">
                  {t('invoices.footer.totalDisplayed')}: <strong>{formatAmount(filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0), stats.currency)}</strong>
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info section */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
          <FiFileText className="w-5 h-5" />
          {t('invoices.info.title')}
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div className="flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <p>{t('invoices.info.autoGenerated')}</p>
          </div>
          <div className="flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <p>{t('invoices.info.emailSent')}</p>
          </div>
          <div className="flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <p>{t('invoices.info.pdfAvailable')}</p>
          </div>
          <div className="flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <p>
              {t('invoices.info.contact')}{' '}
              <a href="mailto:billing@geniac.io" className="underline font-medium hover:text-blue-900">
                billing@geniac.io
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
